import React, { useMemo } from "react";
import { StatusPill } from "@/pvp/components/StatusPill";
import { useApiPvpApplicationsSectionsList } from "@/api/lctApIComponents";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useDimensions } from "@/hooks/useDimensions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useUserAppInfo } from "@/hooks/useUserAppInfo";
import { scrollToElement } from "@/utils/helpers";

interface SideAnchorListProps {
  userAppId: string;
  pvpId: string;
  appId: string;
}

export const PVPSectionList: React.FC<SideAnchorListProps> = ({ userAppId, pvpId, appId }) => {
  const token = useAppSelector((state) => state.user.authKey);
  const { isSmallScreen } = useDimensions();
  const { data: sections } = useApiPvpApplicationsSectionsList(
    { headers: { Authorization: `Token ${token}` }, pathParams: { app: appId, pvp: pvpId } },
    { enabled: !!appId },
  );
  const { appStatus, currentPart } = useUserAppInfo(userAppId);
  const { part1Sections, part2Sections } = useMemo(() => {
    return {
      part1Sections: sections?.results.filter((section) => section.part === 1) ?? [],
      part2Sections: sections?.results.filter((section) => section.part === 2) ?? [],
    };
  }, [sections]);

  const has2Parts = part1Sections.length > 0 && part2Sections.length > 0;

  if (isSmallScreen) {
    return (
      <div className="flex-1">
        <Select onValueChange={(value: string) => scrollToElement(value)}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a section to jump to" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem disabled value="part1" className="font-semibold">
              Part 1
            </SelectItem>
            {part1Sections.map((section) => (
              <SelectItem key={`section-${section.id}`} value={section.id}>
                {`${section.order}. ${section.header_text}`}
              </SelectItem>
            ))}
            {has2Parts ? (
              <>
                <SelectItem disabled value="part2" className="font-semibold">
                  Part 2
                </SelectItem>

                {part2Sections.map((section) => (
                  <SelectItem key={section.id} value={section.id} disabled={currentPart === 1}>
                    {`${section.order}. ${section.header_text}`}
                  </SelectItem>
                ))}
              </>
            ) : null}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <aside className="w-full space-y-8">
      <StatusPill applicationStatus={appStatus} totalParts={has2Parts ? 2 : 1} />

      <>
        <div>
          <div className="space-y-2">
            <h2 className="text-2xl font-medium">Part 1</h2>
          </div>
          <div className="flex flex-col justify-start space-y-2 pt-4">
            {part1Sections.map((section) => (
              <div className="flex w-full flex-row py-1 align-text-top" key={`section-${section.order}`}>
                {`${section.order}. `}
                <Button
                  variant="link"
                  className="flex h-auto w-full justify-start text-wrap pt-0 text-left text-sm font-light text-slate-800"
                  onClick={() => scrollToElement(section.id)}
                >
                  {section.header_text}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {has2Parts ? (
          <div>
            <div className="space-y-2">
              <h2 className="text-2xl font-medium">Part 2</h2>
              {currentPart === 1 ? <StatusPill isLocked /> : null}
            </div>
            <div className="flex flex-col justify-start space-y-2 pt-4">
              {part2Sections.map((section) => (
                <div className="flex w-full flex-row py-1 align-text-top" key={`section-${section.order}`}>
                  {`${section.order}. `}
                  <Button
                    variant="link"
                    className="flex h-auto w-full justify-start text-wrap pt-0 text-left text-sm font-light text-slate-800"
                    onClick={() => scrollToElement(section.id)}
                    disabled={currentPart === 1}
                  >
                    {section.header_text}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </>
    </aside>
  );
};
